import { BulkMessageStatisticsV2ResponseDto } from '@api'
import { addMinutes, format, isSameDay } from 'date-fns'

import { SelectOptionsType } from '@/components/ui/form/FormSelect.tsx'
import { generateFollowUpsTableOptions } from '@/modules/message/components/follow-ups/utils.ts'
import { toPlural } from '@/utils/helpers.ts'

import type { OnlyFansList } from '../creators/types'

const getFormattedTime = (date?: Date): string => {
  const timeFormat = 'hh:mm'

  if (date) {
    return format(date, timeFormat)
  }

  return format(new Date(), timeFormat)
}

const constructDate = (date: Date, time: string, isPM: boolean): Date => {
  const newDate = new Date(date)
  // eslint-disable-next-line prefer-const
  let [hours, minutes] = time.split(':').map(Number)

  // Adjust hours for PM if needed
  if (isPM && hours !== 12) {
    hours += 12
  } else if (!isPM && hours === 12) {
    hours = 0
  }

  // Set the time on the existing date
  newDate.setHours(hours, minutes)

  return newDate
}

const getIsPM = (date: Date): boolean => format(date, 'a') === 'PM'

const isSameMeridiem = (date: Date, fromDate: Date) =>
  format(date, 'a') === format(fromDate, 'a')

const getIsMinTime = (date: Date, fromDate: Date | undefined): boolean => {
  if (!fromDate) {
    return false
  }

  return isSameMeridiem(date, fromDate)
}

const getIsSameDay = (startDate: Date, endDate: Date) =>
  isSameDay(startDate, endDate)

const getIsForbidAM = (date: Date, fromDate: Date | undefined): boolean => {
  if (!fromDate) {
    return false
  }

  const isSameDayValue = getIsSameDay(date, fromDate)

  if (isSameDayValue) {
    return getIsPM(fromDate)
  }

  return false
}

const addMinute = (date: Date): Date => addMinutes(date, 1)

export {
  getFormattedTime,
  constructDate,
  getIsPM,
  getIsMinTime,
  getIsForbidAM,
  getIsSameDay,
  addMinute,
}

export function transformListsToSelectObject(
  lists: Array<OnlyFansList> | undefined,
): SelectOptionsType {
  if (!lists) {
    return []
  }

  return lists.map((list) => ({
    value: list.id,
    label: list.name,
  }))
}

export const processDataForMessageStatistics = (
  data: BulkMessageStatisticsV2ResponseDto,
  title: string,
) => {
  const bulkMessageStatistics = data.bulkMessageStatistics
  const followUpStatistics = data.followUpStatistics
  const rows = [
    {
      label: 'Sent to',
      value: `${bulkMessageStatistics.totalSent ?? 0} ${toPlural(bulkMessageStatistics.totalSent ?? 0, 'Fan')}`,
    },
    {
      label: 'Reads',
      value: `${bulkMessageStatistics.totalReads ?? 0} ${toPlural(bulkMessageStatistics.totalReads ?? 0, 'Fan')}`,
    },
    {
      label: 'Responses',
      value: `${bulkMessageStatistics.totalResponses ?? 0} ${toPlural(bulkMessageStatistics.totalResponses ?? 0, 'Fan')}`,
    },
    {
      label: 'PPV Bought',
      value: `${bulkMessageStatistics.totalPPVBought ?? 0}`,
    },
  ]
  const infoQuickViewFollowUpData =
    generateFollowUpsTableOptions(followUpStatistics)

  const infoQuickView = { title, rows }

  return [infoQuickView, infoQuickViewFollowUpData]
}
